import clsx from 'clsx';
import { FunctionComponent, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button } from '../../../components/common/Button/Button';
import { Card, CardText, CardTitle } from '../../../components/common/Card/Card';
import { PageButtons } from '../../../components/common/PageButtons/PageButtons';
import { PageContainer } from '../../../components/common/PageContainer/PageContainer';
import { Typography } from '../../../components/common/Typography/Typography';
import { FadeInImage } from '../../../components/utils/FadeInImage/FadeInImage';
import { useApi } from '../../../contexts/ApiProvider';
import { useBooking } from '../../../contexts/BookingProvider';
import { useGlobalLoading } from '../../../contexts/GlobalLoadingProvider';
import useApiError from '../../../hooks/useApiError';
import { useBookingParams } from '../../../hooks/useBookingParams';
import { CountryInfo, useCountries } from '../../../hooks/useCountries';
import { Stage } from '../../../model/stage';
import ApiClient from '../../../utils/ApiClient';
import bannerTop from './assets/banner-top.png';
import classes from './PartyDeals.module.scss';

/**
 * The page shown between the party summary and the confirmation page, to give the customer a chance to buy
 * additional party deals.
 */
export const PartyDeals: FunctionComponent = () => {
    const booking = useBooking();
    const { formatMessage } = useIntl();
    const { openStage } = useBookingParams();
    const { currentCountry, getCountryByCulture } = useCountries();
    const { client } = useApi();
    const { incrementLoader, decrementLoader } = useGlobalLoading();
    const { setApiError, clearApiError } = useApiError();

    const [hasPartyDeals, setHasPartyDeals] = useState<boolean>();

    useEffect(() => {
        if (!booking.details?.guid) {
            return;
        }

        const { token, cancel } = ApiClient.createCancelToken();
        clearApiError();
        incrementLoader();

        client
            .hasPartyDeals(booking.details.guid, token)
            .then((response) => setHasPartyDeals(response))
            .catch(setApiError)
            .finally(decrementLoader);

        return cancel;

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [booking.details?.guid]);

    const onPartyDealsButtonClick = () => {
        let countryInfo: CountryInfo = currentCountry;
        if (booking.details) {
            countryInfo = getCountryByCulture(booking.details.country.culture) ?? currentCountry;
        }

        window.location.href = countryInfo.urlProfile + '/party-deals/' + booking.details?.guid;
    };

    const handleConfirmationButtonClick = () => {
        openStage(Stage.ConfirmedBooking, booking.details, true);
    };

    if (hasPartyDeals === undefined) {
        return null;
    }

    if (!hasPartyDeals) {
        handleConfirmationButtonClick();
        return null;
    }

    return (
        <PageContainer title={formatMessage({ id: 'party-deals.title', defaultMessage: 'Party deals!' })}>
            <div className={classes.container}>
                <Card dark className={clsx(classes.card)}>
                    <CardTitle>
                        <FormattedMessage id="party-deals.card-title" defaultMessage="Nu är ditt kalas bokat!" />
                    </CardTitle>
                    <CardText>
                        <FormattedMessage
                            id="party-deals.card-text"
                            defaultMessage="Innan du går vidare till bokningsbekräftelsen och inbjudningarna, vill vi ge dig chansen att uppgradera kalaset med några extra favoriter – till riktigt bra villkor."
                        />
                    </CardText>
                </Card>

                <div className={classes.banner}>
                    <FadeInImage className={classes.inviteBackground} src={bannerTop} />

                    <div className={classes.bannerContent}>
                        <Typography component={'h2'} variant={'title3'} className={classes.bannerTitle}>
                            <FormattedMessage id="party-deals.banner-title" defaultMessage="Party deals för dig!" />
                        </Typography>

                        <p className={classes.bannerPreamble}>
                            <FormattedMessage
                                id="party-deals.banner-preamble"
                                defaultMessage="Du har nu chans att köpa riktigt bra deals med rabatterade priser som gäller på utvalda produkter här och nu."
                            />
                        </p>

                        <p className={classes.bannerText}>
                            <FormattedMessage
                                id="party-deals.banner-text"
                                defaultMessage="Lägg till uppskattade tillval som kaffe och andra favoriter för de vuxna och gör firandet mer avslappnat medan barnen leker."
                            />
                        </p>

                        <Button color="sunnyYellow" className={classes.bannerButton} onClick={onPartyDealsButtonClick}>
                            <FormattedMessage id="party-deals.button-show-deals" defaultMessage="Ja, visa mer!" />
                        </Button>
                    </div>
                </div>
            </div>

            <PageButtons>
                <Button color="transparent" className={classes.skipButton} onClick={handleConfirmationButtonClick}>
                    <FormattedMessage id="party-deals.button-skip" defaultMessage="Till bokningsbekräftelsen" />
                </Button>
            </PageButtons>
        </PageContainer>
    );
};
